import type { MessageableUser } from '@nbh/shared';

/**
 * Lower-cased, underscores and runs of whitespace collapsed to single spaces — the form
 * both the query and the haystack take, so "FRONT_DESK" and "front desk" are one word
 * pair.
 */
export function normalise(value: string): string {
  return value.toLocaleLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Whether a directory row answers a search.
 *
 * Every word the person typed has to start some word of the row — the name, the
 * department (raw and as labelled) or the role — so "bang" finds every Bangura, "recep"
 * finds Reception, and "front" finds everyone on the front desk whether they typed the
 * department or the role. Case never matters. An empty query matches everyone.
 */
export function matchesQuery(
  user: MessageableUser,
  query: string,
  departmentLabel: string,
): boolean {
  const tokens = normalise(query).split(' ').filter(Boolean);
  if (tokens.length === 0) return true;

  const words = normalise(`${user.name} ${departmentLabel} ${user.department} ${user.role}`).split(
    ' ',
  );
  return tokens.every((token) => words.some((word) => word.startsWith(token)));
}

/**
 * Desk logins that several people sit behind — Reception, Events, F&B — as distinct
 * from a person.
 *
 * Recognised by name, because the account model has no flag for it (DEBT-23). The list
 * is the desks this property actually runs and is matched on the normalised whole
 * name, so "Abu Reception" is still a person. When the flag lands, this becomes a
 * one-line read of it and the list goes.
 */
export const SHARED_ACCOUNT_NAMES: ReadonlySet<string> = new Set([
  'reception',
  'front desk',
  'front office',
  'events',
  'f&b',
  'f & b',
  'food & beverage',
  'food and beverage',
  'kitchen',
  'restaurant',
  'bar',
  'housekeeping',
  'maintenance',
  'engineering',
  'security',
  'it',
  'it desk',
  'night desk',
  'concierge',
  'laundry',
  'spa',
]);

export function isSharedAccount(user: Pick<MessageableUser, 'name'>): boolean {
  return SHARED_ACCOUNT_NAMES.has(normalise(user.name));
}
